import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff, CheckCircle, XCircle, Mail, Shield, User, Phone, Loader2 } from 'lucide-react';
import { LottieLoading } from '@/components/ui/lottie-loading';
import {
  validateInvitationToken,
  verifyInvitationCode,
  acceptInvitation,
} from '@/lib/adminApi';

type Step = 'loading' | 'verify-code' | 'complete-profile' | 'success' | 'error';

interface InvitationData {
  email: string;
  company_name: string;
  groups: Array<{ group_id: string; group_name: string }>;
  role: string;
  inviter_name?: string;
  message?: string;
  expires_at: string;
  requires_password: boolean;
}

export function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // State
  const [step, setStep] = useState<Step>('loading');
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Code verification
  const [code, setCode] = useState(['', '', '', '']);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [, setCodeVerified] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [, setAttemptsRemaining] = useState<number | null>(null);
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);

  // Profile completion
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Success state
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Validate token on mount
  useEffect(() => {
    async function validate() {
      if (!token) {
        setError('No invitation token provided. Please check your invitation link.');
        setStep('error');
        return;
      }

      try {
        const response = await validateInvitationToken(token);
        if (response.valid && response.invitation) {
          setInvitation(response.invitation);
          setStep('verify-code');
        } else {
          setError('This invitation link is invalid or has expired.');
          setStep('error');
        }
      } catch (err: unknown) {
        setError((err as Error)?.message || 'Failed to validate invitation. Please try again.');
        setStep('error');
      }
    }

    validate();
  }, [token]);

  // Handle code input
  const handleCodeChange = (index: number, value: string) => {
    // Only allow alphanumeric (uppercase)
    const cleaned = value.toUpperCase().replace(/[^A-Z2-9]/g, '');
    if (cleaned.length <= 1) {
      const newCode = [...code];
      newCode[index] = cleaned;
      setCode(newCode);
      setCodeError(null);

      // Auto-focus next input
      if (cleaned && index < 3) {
        const nextInput = document.getElementById(`code-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      prevInput?.focus();
    }
  };

  // Verify code
  const handleVerifyCode = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 4) {
      setCodeError('Please enter the complete 4-digit code');
      return;
    }

    setVerifyingCode(true);
    setCodeError(null);

    try {
      const response = await verifyInvitationCode(token!, fullCode);

      if (response.valid) {
        setCodeVerified(true);
        setStep('complete-profile');
      } else {
        if (response.locked_until) {
          setLockedUntil(response.locked_until);
          setCodeError('Too many failed attempts. Please try again later.');
        } else {
          setAttemptsRemaining(response.attempts_remaining ?? null);
          setCodeError(
            response.attempts_remaining
              ? `Invalid code. ${response.attempts_remaining} attempts remaining.`
              : 'Invalid verification code.'
          );
        }
        // Clear code inputs
        setCode(['', '', '', '']);
        document.getElementById('code-0')?.focus();
      }
    } catch (err: unknown) {
      setCodeError((err as Error)?.message || 'Failed to verify code. Please try again.');
    } finally {
      setVerifyingCode(false);
    }
  };

  // Submit profile and accept invitation
  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError(null);

    // Validation
    if (!firstName.trim()) {
      setProfileError('Please enter your first name');
      return;
    }
    if (!lastName.trim()) {
      setProfileError('Please enter your last name');
      return;
    }

    if (invitation?.requires_password) {
      if (!password) {
        setProfileError('Password is required for staff companies');
        return;
      }
      if (password.length < 8) {
        setProfileError('Password must be at least 8 characters');
        return;
      }
      if (password !== confirmPassword) {
        setProfileError('Passwords do not match');
        return;
      }
    }

    setSubmitting(true);

    try {
      const response = await acceptInvitation({
        token: token!,
        code: code.join(''),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || undefined,
        password: invitation?.requires_password ? password : undefined,
      });

      if (response.success) {
        setMagicLinkSent(response.magic_link_sent ?? false);
        setStep('success');
      } else {
        setProfileError('Failed to accept invitation. Please try again.');
      }
    } catch (err: unknown) {
      setProfileError((err as Error)?.message || 'An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Role display
  const roleLabels: Record<string, string> = {
    user: 'User',
    group_admin: 'Group Admin',
    company_admin: 'Company Admin',
    admin: 'Admin',
    superadmin: 'Superadmin',
  };

  // Render loading state
  if (step === 'loading') {
    return <LottieLoading message="Validating invitation..." />;
  }

  // Render error state
  if (step === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md bg-card/50 border-border">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <XCircle className="h-16 w-16 text-red-500" />
            </div>
            <CardTitle className="text-xl text-foreground">Invalid Invitation</CardTitle>
            <CardDescription className="text-muted-foreground">
              {error}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              If you believe this is an error, please contact the person who sent you the invitation.
            </p>
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              className="w-full border-border text-muted-foreground hover:bg-accent"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render success state
  if (step === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md bg-card/50 border-border">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <CheckCircle className="h-16 w-16 text-[#8FB560]" />
            </div>
            <CardTitle className="text-xl text-foreground">Welcome to {invitation?.company_name}!</CardTitle>
            <CardDescription className="text-muted-foreground">
              Your account has been created successfully.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {magicLinkSent ? (
              <>
                <div className="bg-blue-950/30 border border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-blue-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-200 font-medium">Check your email</p>
                      <p className="text-xs text-blue-300/80 mt-1">
                        We've sent a magic link to <strong>{invitation?.email}</strong>.
                        Click the link in your email to sign in.
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Didn't receive the email? Check your spam folder or contact support.
                </p>
              </>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-[#8FB560] hover:bg-[#7da350] text-black"
              >
                Sign In Now
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/assets/ht-logo.png"
            alt="Hollywood Trucks"
            className="h-20 w-auto"
          />
        </div>

        <Card className="bg-card/50 border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">
              {step === 'verify-code' ? 'Verify Your Identity' : 'Complete Your Profile'}
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {step === 'verify-code'
                ? 'Enter the 4-digit verification code you received'
                : `Join ${invitation?.company_name} as ${roleLabels[invitation?.role || 'user']}`
              }
            </CardDescription>
          </CardHeader>

          <CardContent>
            {/* Invitation details */}
            {invitation && (
              <div className="mb-6 p-3 rounded-lg bg-background/50 border border-border">
                <div className="text-xs text-muted-foreground mb-2">You've been invited to:</div>
                <div className="text-sm text-foreground font-medium">{invitation.company_name}</div>
                {invitation.groups.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Groups: {invitation.groups.map(g => g.group_name).join(', ')}
                  </div>
                )}
                {invitation.inviter_name && (
                  <div className="text-xs text-muted-foreground mt-2">
                    Invited by: {invitation.inviter_name}
                  </div>
                )}
                {invitation.message && (
                  <div className="mt-2 p-2 rounded bg-amber-950/30 border border-amber-800/50">
                    <p className="text-xs text-amber-200/80 italic">"{invitation.message}"</p>
                  </div>
                )}
              </div>
            )}

            {/* Step: Verify Code */}
            {step === 'verify-code' && (
              <div className="space-y-6">
                <div className="bg-background/30 border border-border rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-[#8FB560] mt-0.5" />
                    <div>
                      <p className="text-sm text-foreground font-medium">Verification Required</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        The person who invited you should have provided a 4-digit verification code
                        (via text, phone call, or in person).
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-foreground mb-3 block">Enter verification code</Label>
                  <div className="flex justify-center gap-3">
                    {code.map((digit, index) => (
                      <Input
                        key={index}
                        id={`code-${index}`}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                        disabled={verifyingCode || !!lockedUntil}
                        className="w-14 h-14 text-center text-2xl font-mono bg-background/50 border-border text-foreground"
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>
                </div>

                {codeError && (
                  <div className="text-sm text-red-400 bg-red-950/30 border border-red-900 rounded-md p-3">
                    {codeError}
                  </div>
                )}

                {lockedUntil && (
                  <div className="text-sm text-amber-400 bg-amber-950/30 border border-amber-900 rounded-md p-3">
                    Account locked due to too many failed attempts. Please try again after{' '}
                    {new Date(lockedUntil).toLocaleTimeString()}.
                  </div>
                )}

                <Button
                  onClick={handleVerifyCode}
                  disabled={code.join('').length !== 4 || verifyingCode || !!lockedUntil}
                  className="w-full bg-[#8FB560] hover:bg-[#7da350] text-black"
                >
                  {verifyingCode ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </Button>
              </div>
            )}

            {/* Step: Complete Profile */}
            {step === 'complete-profile' && (
              <form onSubmit={handleAccept} className="space-y-4">
                {/* Verified badge */}
                <div className="flex items-center gap-2 text-[#8FB560] text-sm mb-4">
                  <CheckCircle className="h-4 w-4" />
                  <span>Code verified successfully</span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-foreground">
                      <User className="h-4 w-4 inline mr-2" />
                      First Name *
                    </Label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      disabled={submitting}
                      className="bg-background/50 border-border text-foreground placeholder:text-muted-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-foreground">
                      Last Name *
                    </Label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Smith"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      disabled={submitting}
                      className="bg-background/50 border-border text-foreground placeholder:text-muted-foreground"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-foreground">
                    <Phone className="h-4 w-4 inline mr-2" />
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={submitting}
                    className="bg-background/50 border-border text-foreground placeholder:text-muted-foreground"
                  />
                </div>

                {/* Password fields - only for HT staff */}
                {invitation?.requires_password && (
                  <>
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs text-amber-400 mb-3">
                        Staff accounts require a password for security
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-foreground">
                        Password *
                      </Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          disabled={submitting}
                          className="bg-background/50 border-border text-foreground placeholder:text-muted-foreground pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">Minimum 8 characters</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-foreground">
                        Confirm Password *
                      </Label>
                      <Input
                        id="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={submitting}
                        className="bg-background/50 border-border text-foreground placeholder:text-muted-foreground"
                      />
                    </div>
                  </>
                )}

                {profileError && (
                  <div className="text-sm text-red-400 bg-red-950/30 border border-red-900 rounded-md p-3">
                    {profileError}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#8FB560] hover:bg-[#7da350] text-black"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    'Accept Invitation'
                  )}
                </Button>

                {!invitation?.requires_password && (
                  <p className="text-xs text-muted-foreground text-center">
                    After accepting, we'll send you a magic link to sign in.
                  </p>
                )}
              </form>
            )}
          </CardContent>
        </Card>

        {/* Email display */}
        <p className="text-center text-xs text-muted-foreground mt-4">
          Invitation for: <span className="text-foreground">{invitation?.email}</span>
        </p>
      </div>
    </div>
  );
}

export default AcceptInvite;
